import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CartItem, CandleProduct } from './types';
import { productService, authService, authGuardService } from './shared/services';
import { productToCandleProduct } from './shared/utils';
import { useIsMobile } from './hooks/useIsMobile';
// Code Splitting: Lazy loaded experience containers, modals and shop view
import { DesktopExperience } from './components/desktop/DesktopExperience';
import { MobileExperience } from './components/mobile/MobileExperience';
const CartDrawer = React.lazy(() =>
  import('./components/CartDrawer').then((m) => ({ default: m.CartDrawer }))
);
import { RashmiStoryModal } from './components/RashmiStoryModal';
import { FragranceWorldModal } from './components/FragranceWorldModal';
const AccountModal = React.lazy(() =>
  import('./components/AccountModal').then((m) => ({ default: m.AccountModal }))
);
const CinematicShopTransition = React.lazy(() =>
  import('./components/CinematicShopTransition').then((m) => ({ default: m.CinematicShopTransition }))
);
const ShopView = React.lazy(() =>
  import('./components/ShopView').then((m) => ({ default: m.ShopView }))
);
const ProductDetailPage = React.lazy(() =>
  import('./components/ProductDetailPage').then((m) => ({ default: m.ProductDetailPage }))
);
const AdminApp = React.lazy(() =>
  import('./admin/AdminApp').then((m) => ({ default: m.AdminApp }))
);
const CheckoutPage = React.lazy(() =>
  import('./components/checkout/CheckoutPage').then((m) => ({ default: m.CheckoutPage }))
);

gsap.registerPlugin(ScrollTrigger);

export default function App() {
  const isMobile = useIsMobile(768);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeCandleIndex, setActiveCandleIndex] = useState(0);

  // Mode: 'website' or 'admin'
  const [viewMode, setViewMode] = useState<'website' | 'admin'>(() => {
    if (typeof window !== 'undefined') {
      return window.location.hash === '#admin' || window.location.pathname.startsWith('/admin')
        ? 'admin'
        : 'website';
    }
    return 'website';
  });

  // Shop View & Product Route State
  const [isShopTransitioning, setIsShopTransitioning] = useState(false);
  const [isShopActive, setIsShopActive] = useState(false);
  const [clickOrigin, setClickOrigin] = useState<{ x: number; y: number } | null>(null);

  // Modals and Drawer state
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isFounderOpen, setIsFounderOpen] = useState(false);
  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [accountInitialTab, setAccountInitialTab] = useState<any>('account');
  const [accountAuthMode, setAccountAuthMode] = useState<'signin' | 'signup' | null>(null);

  // Dedicated Product Details Page Route State
  const [selectedProductSlug, setSelectedProductSlug] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      if (pathname.startsWith('/products/')) {
        return pathname.replace('/products/', '');
      }
      const hash = window.location.hash;
      if (hash.startsWith('#product/')) {
        return hash.replace('#product/', '');
      }
    }
    return null;
  });

  // Cart State with Guest Storage Persistence
  const GUEST_CART_KEY = 'noor_guest_cart';

  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const saved = localStorage.getItem(GUEST_CART_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Failed to load guest cart from localStorage:', e);
    }
    return [];
  });

  // Listen to location hash changes for #admin
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#admin' || window.location.pathname.startsWith('/admin')) {
        if (!authService.isLoggedIn()) {
          if (import.meta.env.DEV) {
            console.log('[AuthGuard] Protected route blocked for guest user: Admin Route (#admin)');
          }
          authGuardService.requireAuth(() => {
            setViewMode('admin');
          }, {
            tab: 'account',
            mode: 'signin',
            notice: 'Sign in or create an account to access the Admin Panel.',
          });
        } else {
          setViewMode('admin');
        }
      } else {
        setViewMode('website');
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleOpenAdmin = useCallback(() => {
    if (authService.isLoggedIn()) {
      window.location.hash = 'admin';
      setViewMode('admin');
    } else {
      if (import.meta.env.DEV) {
        console.log('[AuthGuard] Protected action blocked for guest: Admin Panel');
      }
      authGuardService.requireAuth(() => {
        window.location.hash = 'admin';
        setViewMode('admin');
      }, {
        tab: 'account',
        mode: 'signin',
        notice: 'Sign in or create an account to access the Admin Panel.',
      });
    }
  }, []);

  const handleBackToWebsite = () => {
    if (window.location.hash === '#admin') {
      window.history.pushState('', document.title, window.location.pathname + window.location.search);
    }
    setViewMode('website');
  };

  // Listen to browser Back/Forward popstate & hash change for product routes
  useEffect(() => {
    const handleUrlRoute = () => {
      if (typeof window === 'undefined') return;
      const pathname = window.location.pathname;
      if (pathname.startsWith('/products/')) {
        const slug = pathname.replace('/products/', '');
        setSelectedProductSlug(slug);
      } else if (window.location.hash.startsWith('#product/')) {
        const slug = window.location.hash.replace('#product/', '');
        setSelectedProductSlug(slug);
      } else {
        setSelectedProductSlug(null);
      }
    };

    window.addEventListener('popstate', handleUrlRoute);
    window.addEventListener('hashchange', handleUrlRoute);
    return () => {
      window.removeEventListener('popstate', handleUrlRoute);
      window.removeEventListener('hashchange', handleUrlRoute);
    };
  }, []);

  // Handle Selecting a Product for Dedicated Product Page Navigation
  const handleSelectProduct = useCallback((slugOrId: string, origin?: { x: number; y: number } | React.MouseEvent) => {
    if (origin && 'clientX' in origin) {
      setClickOrigin({ x: origin.clientX, y: origin.clientY });
    } else if (origin && typeof (origin as { x: number }).x === 'number') {
      setClickOrigin(origin as { x: number; y: number });
    } else {
      setClickOrigin({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    }

    const cleanUrl = `/products/${slugOrId}`;
    if (window.location.pathname !== cleanUrl) {
      window.history.pushState({ productSlug: slugOrId }, '', cleanUrl);
    }
    setSelectedProductSlug(slugOrId);
    setIsCartOpen(false);
  }, []);

  const handleBackToShopFromProduct = useCallback(() => {
    setSelectedProductSlug(null);
    setIsShopActive(true);
    if (window.location.pathname.startsWith('/products/')) {
      window.history.pushState({}, '', '/#collection');
    }
  }, []);

  const handleBackToHomeFromProduct = useCallback(() => {
    setSelectedProductSlug(null);
    setIsShopActive(true);
    if (window.location.pathname.startsWith('/products/')) {
      window.history.pushState({}, '', '/');
    }
  }, []);



  // Sync cart state to localStorage whenever cartItems changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (cartItems.length > 0) {
        localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
      } else {
        localStorage.removeItem(GUEST_CART_KEY);
      }
    } catch (e) {
      console.error('Failed to save guest cart to localStorage:', e);
    }
  }, [cartItems]);

  const hasIgnitedRef = useRef(false);
  const lastProgressRef = useRef(0);
  const lenisRef = useRef<Lenis | null>(null);

  // Idle preloading for lazy chunks
  useEffect(() => {
    const preload = () => {
      import('./components/CartDrawer');
      import('./components/AccountModal');
      import('./components/ShopView');
    };
    if ('requestIdleCallback' in window) {
      (window as unknown as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(preload);
    } else {
      setTimeout(preload, 1200);
    }
  }, []);

  // Initialize Lenis + GSAP ScrollTrigger global smooth scroll engine
  useEffect(() => {
    const lenis = new Lenis({
      duration: isMobile ? 1.0 : 1.5,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: isMobile ? 0.85 : 0.95,
      touchMultiplier: isMobile ? 1.1 : 1.3,
      prevent: (node) => {
        if (!node || !(node instanceof HTMLElement)) return false;
        return (
          node.hasAttribute('data-lenis-prevent') ||
          Boolean(
            node.closest(
              '[data-lenis-prevent], .overflow-y-auto, .overflow-auto, .overflow-y-scroll, .overflow-scroll, [role="dialog"], .admin-panel, .modal-card, .fixed'
            )
          )
        );
      },
    });

    lenisRef.current = lenis;
    (window as any).__lenis = lenis;

    // Explicitly initialize Lenis scroll target to 0 on initial page load
    lenis.scrollTo(0, { immediate: true });

    // Update GSAP ScrollTrigger when Lenis emits scroll events
    lenis.on('scroll', () => {
      ScrollTrigger.update();
    });

    // High precision requestAnimationFrame loop for Lenis RAF
    let rafId: number | null = null;
    const updateLenis = (time: number) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(updateLenis);
    };
    rafId = requestAnimationFrame(updateLenis);

    gsap.ticker.lagSmoothing(0);

    // Initial Lenis dimension recalculation & ScrollTrigger refresh
    lenis.resize();
    ScrollTrigger.refresh();

    // Pause Lenis RAF loop when document is hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lenis.stop();
        if (rafId !== null) cancelAnimationFrame(rafId);
      } else {
        lenis.start();
        lenis.resize();
        rafId = requestAnimationFrame(updateLenis);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (rafId !== null) cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
      delete (window as any).__lenis;
    };
  }, [isMobile]);

  // Pause Lenis when Admin view or popups are active to guarantee 100% smooth native scrolling
  useEffect(() => {
    const handleRouteLenisToggle = () => {
      const lenis = (window as any).__lenis;
      if (!lenis) return;
      if (typeof window !== 'undefined' && window.location.hash.startsWith('#admin')) {
        lenis.stop();
      } else if (!isFounderOpen && !isCustomizerOpen && !isAccountOpen && !isCartOpen) {
        lenis.start();
      }
    };
    handleRouteLenisToggle();
    window.addEventListener('hashchange', handleRouteLenisToggle);
    window.addEventListener('popstate', handleRouteLenisToggle);
    return () => {
      window.removeEventListener('hashchange', handleRouteLenisToggle);
      window.removeEventListener('popstate', handleRouteLenisToggle);
    };
  }, [isFounderOpen, isCustomizerOpen, isAccountOpen, isCartOpen]);



  // Handle Scroll Progress with direct GSAP ScrollTrigger & window scroll event fail-safe synchronization
  useEffect(() => {
    if (isShopActive) return;

    let refreshRafId: number | null = null;

    const updateProgress = (rawProgress: number) => {
      const progress = Math.min(1, Math.max(0, rawProgress));
      lastProgressRef.current = progress;
      setScrollProgress(progress);

      // Trigger match strike sound when user reaches ignition scene for the first time
      if (progress >= 0.07 && !hasIgnitedRef.current) {
        hasIgnitedRef.current = true;
      }
    };

    const scrollTrigger = ScrollTrigger.create({
      trigger: document.body,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: (self) => {
        updateProgress(self.progress);
      },
    });

    // Native window scroll listener fallback to guarantee progress updates even during initial load
    const handleNativeWindowScroll = () => {
      const totalScrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (totalScrollable > 0) {
        const nativeRatio = window.scrollY / totalScrollable;
        updateProgress(nativeRatio);
      }
    };

    window.addEventListener('scroll', handleNativeWindowScroll, { passive: true });

    const forceResizeAndRefresh = () => {
      if (lenisRef.current) {
        lenisRef.current.resize();
      }
      ScrollTrigger.refresh();
    };

    // Recalculate Lenis limits & ScrollTrigger height whenever document layout changes
    const resizeObserver = new ResizeObserver(forceResizeAndRefresh);
    resizeObserver.observe(document.body);

    refreshRafId = requestAnimationFrame(forceResizeAndRefresh);

    return () => {
      if (refreshRafId !== null) cancelAnimationFrame(refreshRafId);
      window.removeEventListener('scroll', handleNativeWindowScroll);
      resizeObserver.disconnect();
      scrollTrigger.kill();
    };
  }, [isShopActive, isMobile]);

  // Trigger Shop Transition
  const handleTriggerShopTransition = useCallback((origin?: { x: number; y: number } | React.MouseEvent) => {
    if (origin && 'clientX' in origin) {
      setClickOrigin({ x: origin.clientX, y: origin.clientY });
    } else if (origin && typeof (origin as { x: number }).x === 'number' && typeof (origin as { y: number }).y === 'number') {
      setClickOrigin(origin as { x: number; y: number });
    } else {
      setClickOrigin({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    }
    setIsShopTransitioning(true);
  }, []);

  const handleRevealShop = useCallback(() => {
    setIsShopActive(true);

    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
  }, []);

  const handleTransitionComplete = useCallback(() => {
    setIsShopTransitioning(false);
  }, []);

  const handleBackToStory = useCallback(() => {
    setIsShopActive(false);

    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { duration: 1.0 });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
  }, []);

  // Programmatic Scroll Seek using Lenis smooth scroll
  const handleSeekToProgress = useCallback((targetRatio: number) => {
    const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
    const targetY = targetRatio * totalHeight;

    if (lenisRef.current) {
      lenisRef.current.scrollTo(targetY, {
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      });
    } else {
      window.scrollTo({
        top: targetY,
        behavior: 'smooth',
      });
    }
  }, []);

  // Register AuthGuard modal opener
  useEffect(() => {
    authGuardService.registerModalHandler((tab, mode) => {
      setAccountInitialTab(tab || 'account');
      setAccountAuthMode(mode || 'signin');
      setIsAccountOpen(true);
    });
    return () => {
      authGuardService.unregisterModalHandler();
    };
  }, []);

  // Memoized Handlers for Modals
  const handleOpenFounderModal = useCallback(() => setIsFounderOpen(true), []);
  const handleOpenCustomizerModal = useCallback(() => {
    setIsCustomizerOpen(true);
  }, []);

  const handleOpenAccount = useCallback((tab: any = 'account', mode: 'signin' | 'signup' | null = null) => {
    if (authService.isLoggedIn()) {
      setAccountInitialTab(tab);
      setAccountAuthMode(mode);
      setIsAccountOpen(true);
    } else {
      if (tab && tab !== 'account') {
        authGuardService.requireAuth(() => {
          setAccountInitialTab(tab);
          setAccountAuthMode(null);
          setIsAccountOpen(true);
        }, {
          tab: tab,
          mode: mode || 'signin',
          notice: tab === 'wishlist' ? 'Sign in or create an account to view your Saved Wishlist.'
                : tab === 'orders' ? 'Sign in or create an account to view your Order History.'
                : tab === 'addresses' ? 'Sign in or create an account to manage Saved Addresses.'
                : tab === 'payments' ? 'Sign in or create an account to manage Payment Methods.'
                : 'Sign in or create an account to access your Atelier Sanctuary.'
        });
      } else {
        setAccountInitialTab('account');
        setAccountAuthMode(mode || 'signin');
        setIsAccountOpen(true);
      }
    }
  }, []);

  // Cart Handlers
  const handleOpenCart = useCallback((candle?: CandleProduct) => {
    if (candle) {
      if (!authService.isLoggedIn() && import.meta.env.DEV) {
        console.log(`[AuthGuard] Protected action blocked for guest: Add "${candle.name}" to shopping bag`);
      }
      authGuardService.requireAuth(() => {
        setCartItems((prev) => {
          const existing = prev.find((item) => item.candle.id === candle.id);
          if (existing) {
            return prev.map((item) =>
              item.candle.id === candle.id
                ? { ...item, quantity: item.quantity + 1 }
                : item
            );
          }
          return [...prev, { candle, quantity: 1 }];
        });
        setIsCartOpen(true);
      }, { notice: `Sign in or create an account to add "${candle.name}" to your shopping bag.` });
    } else {
      setIsCartOpen(true);
    }
  }, []);

  const handleUpdateQuantity = useCallback((candleId: string, delta: number) => {
    setCartItems((prev) =>
      prev
        .map((item) => {
          if (item.candle.id === candleId) {
            const newQty = item.quantity + delta;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter(Boolean) as CartItem[]
    );
  }, []);

  const handleRemoveItem = useCallback((candleId: string) => {
    setCartItems((prev) => prev.filter((item) => item.candle.id !== candleId));
  }, []);

  const handleClearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const handleAddCustomToCart = useCallback((customCandle: CandleProduct) => {
    handleOpenCart(customCandle);
  }, [handleOpenCart]);

  const handleExploreCollection = useCallback(() => {
    if (!isShopActive) {
      handleTriggerShopTransition();
    } else {
      const el = document.getElementById('collection');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isShopActive, handleTriggerShopTransition]);

  const cartItemsCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  if (viewMode === 'admin') {
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#110F0E] text-[#F8F3EB] flex items-center justify-center font-serif text-[#C8A45A]">Loading Atelier Admin Panel...</div>}>
        <AdminApp onBackToWebsite={handleBackToWebsite} />
      </Suspense>
    );
  }

  return (
    <>


      {/* Fullscreen Cinematic Golden Light Expansion Transition */}
      <Suspense fallback={null}>
        {isShopTransitioning && (
          <CinematicShopTransition
            isActive={isShopTransitioning}
            clickOrigin={clickOrigin}
            onRevealShop={handleRevealShop}
            onComplete={handleTransitionComplete}
          />
        )}
      </Suspense>

      {/* Main Experience: Full-Screen Luxury Client Lounge Account Page, Shopping Bag Page, Dedicated Product Details Page, Luxury Shop View, or Continuous Story View */}
      {isCheckoutOpen ? (
        <Suspense fallback={<div className="min-h-screen bg-[#FFFDF9]" />}>
          <CheckoutPage
            cartItems={cartItems}
            onClearCart={handleClearCart}
            onBackToCart={() => {
              setIsCheckoutOpen(false);
              setIsCartOpen(true);
            }}
            onContinueShopping={() => {
              setIsCheckoutOpen(false);
              setIsShopActive(true);
            }}
            onViewOrders={() => {
              setIsCheckoutOpen(false);
              handleOpenAccount('orders');
            }}
          />
        </Suspense>
      ) : isAccountOpen ? (
        <Suspense fallback={<div className="min-h-screen bg-[#FAF7F2]" />}>
          <AccountModal
            isOpen={isAccountOpen}
            onClose={() => {
              setIsAccountOpen(false);
            }}
            onAddToCart={(candle) => handleOpenCart(candle)}
            initialTab={accountInitialTab}
            initialAuthMode={accountAuthMode}
            cartCount={cartItemsCount}
            onOpenCart={handleOpenCart}
          />
        </Suspense>
      ) : isCartOpen ? (
        <Suspense fallback={<div className="min-h-screen bg-[#F8F3EB]" />}>
          <CartDrawer
            isOpen={isCartOpen}
            onClose={() => setIsCartOpen(false)}
            items={cartItems}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveItem}
            onClearCart={handleClearCart}
            onExploreCollection={handleExploreCollection}
            onAddToCart={(candle) => handleOpenCart(candle)}
            onOpenAccount={handleOpenAccount}
            onProceedToCheckout={() => setIsCheckoutOpen(true)}
          />
        </Suspense>
      ) : selectedProductSlug ? (
        <Suspense fallback={<div className="min-h-screen bg-[#F8F3EB]" />}>
          <ProductDetailPage
            productSlugOrId={selectedProductSlug}
            onBackToShop={handleBackToShopFromProduct}
            onBackToHome={handleBackToHomeFromProduct}
            onOpenCart={handleOpenCart}
            onOpenFounderModal={handleOpenFounderModal}
            onOpenCustomizerModal={handleOpenCustomizerModal}
            onSelectProduct={handleSelectProduct}
            cartCount={cartItemsCount}
            onOpenAccount={handleOpenAccount}
          />
        </Suspense>
      ) : isShopActive ? (
        <Suspense fallback={<div className="min-h-screen bg-[#F8F3EB]" />}>
          <ShopView
            onOpenCart={handleOpenCart}
            onOpenFounderModal={handleOpenFounderModal}
            onOpenCustomizerModal={handleOpenCustomizerModal}
            onBackToStory={handleBackToStory}
            cartCount={cartItemsCount}
            onTriggerShopTransition={handleTriggerShopTransition}
            onOpenAccount={handleOpenAccount}
            onSelectProduct={handleSelectProduct}
          />
        </Suspense>
      ) : isMobile ? (
        <MobileExperience
          scrollProgress={scrollProgress}
          activeCandleIndex={activeCandleIndex}
          setActiveCandleIndex={setActiveCandleIndex}
          onOpenCart={handleOpenCart}
          onOpenFounderModal={handleOpenFounderModal}
          onOpenCustomizerModal={handleOpenCustomizerModal}
          onScrollToProgress={handleSeekToProgress}
          cartItemsCount={cartItemsCount}
          onTriggerShopTransition={handleTriggerShopTransition}
          onOpenAccount={handleOpenAccount}
        />
      ) : (
        <DesktopExperience
          scrollProgress={scrollProgress}
          activeCandleIndex={activeCandleIndex}
          setActiveCandleIndex={setActiveCandleIndex}
          onOpenCart={handleOpenCart}
          onOpenFounderModal={handleOpenFounderModal}
          onOpenCustomizerModal={handleOpenCustomizerModal}
          onScrollToProgress={handleSeekToProgress}
          onTriggerShopTransition={handleTriggerShopTransition}
          onOpenAccount={handleOpenAccount}
          cartCount={cartItemsCount}
        />
      )}

      {/* Shared Modals and Drawers */}
      <RashmiStoryModal
        isOpen={isFounderOpen}
        onClose={() => setIsFounderOpen(false)}
      />

      <FragranceWorldModal
        isOpen={isCustomizerOpen}
        onClose={() => setIsCustomizerOpen(false)}
        onAddCustomToCart={handleAddCustomToCart}
      />
    </>
  );
}
